import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { Card, Col, Form, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { useParams } from "react-router-dom";

// Icons
import Icon from "@mdi/react";
import { mdiCheck } from "@mdi/js";
import { goalIcons } from "../../../../components/common/sDgs";

// redux
import {
  createCertifyProfileProject,
  updateCertifyProfileProject,
} from "../../../../redux/slices/utilsSlice";

const ProjectInfo = () => {
  const dispatch = useDispatch();
  const routeParams = useParams();

  const { stages, projectId } = routeParams;
  const {
    project,
    certifyProjectProfile,
    projectCategories,
    goals,
    mainSectorOptions,
    sectionOptions,
    departmentOptions,
    programs,
    mainProjects,
  } = useSelector((state) => ({
    project: state.certifyReducer.project.data,
    certifyProjectProfile: state.utilsReducer.certifyProjectProfile,
    projectCategories: state.utilsReducer.dropdownItems.categories,
    goals: state.utilsReducer.checkBoxItems.sustainableDevelopmentGoals,
    mainSectorOptions: state.utilsReducer.dropdownItems.mainSectorOptions,
    sectionOptions: state.utilsReducer.dropdownItems.upen_sections,
    departmentOptions: state.utilsReducer.dropdownItems.departments,

    programs: state.utilsReducer.dropdownItems.programs,
    mainProjects: state.utilsReducer.dropdownItems.mainProjects,
  }));

  const [showColumn, setShowColumn] = useState({
    jppn: false,
    cabinet: false,
  });
  const [strategicDirectionOptions, setStrategicDirectionOptions] = useState(
    [],
  );
  const [programOptions, setProgramOptions] = useState([]);
  const [mainProjectOptions, setMainProjectOptions] = useState([]);

  const priorityOptions = Array.from({ length: 300 }, (_, i) => i + 1).map(
    (value) => ({
      value,
      label: value,
    }),
  );

  const handleSelectGoal = (item) => {
    const value = Number(item.index_no);

    const isExisted =
      certifyProjectProfile.sustainable_development_goals.includes(value);

    if (isExisted) {
      const newValues = {
        name: "sustainable_development_goals",
        value: certifyProjectProfile.sustainable_development_goals.filter(
          (obj) => obj !== value,
        ),
      };
      dispatch(updateCertifyProfileProject(newValues));
    } else {
      const newValues = {
        name: "sustainable_development_goals",
        value: [...certifyProjectProfile.sustainable_development_goals, value],
      };
      dispatch(updateCertifyProfileProject(newValues));
    }
  };

  const handleCheckPriorityAreaItem = (e, item) => {
    const isExisted = certifyProjectProfile.strategic_directions
      ?.map((obj) => obj.strategic_direction_id)
      .includes(item.id);

    const selected = {
      project_id: project.id,
      strategic_direction_id: item.id,
      main_sector_id: null,
      selected: null,
      data: item,
    };

    if (isExisted) {
      const newValues = {
        name: "strategic_directions",
        value: certifyProjectProfile.strategic_directions.filter(
          (obj) => obj.strategic_direction_id !== item.id,
        ),
      };
      dispatch(updateCertifyProfileProject(newValues));
    } else {
      const newValues = {
        name: "strategic_directions",
        value: [...certifyProjectProfile.strategic_directions, selected],
      };
      dispatch(updateCertifyProfileProject(newValues));
    }
  };

  const handleSelectPriorityArea = (value) => {
    const newValues = {
      name: "main_sector",
      value: value,
    };
    setStrategicDirectionOptions(value?.strategic_directions);
    dispatch(updateCertifyProfileProject(newValues));
  };

  const handleSelectUpenSection = (value) => {
    const newValues = {
      name: "upen_section",
      value: value,
    };
    dispatch(updateCertifyProfileProject(newValues));
  };

  const handleSelectApplicantAgency = (value) => {
    const newValues = {
      name: "applicant_agency",
      value: value,
    };
    dispatch(updateCertifyProfileProject(newValues));
  };

  const handleSelectExecutingAgency = (value) => {
    const newValues = {
      name: "executing_agency",
      value: value,
    };
    dispatch(updateCertifyProfileProject(newValues));
  };

  const handleSelectFinancialAgency = (value) => {
    const newValues = {
      name: "financially_responsible_agency",
      value: value,
    };
    dispatch(updateCertifyProfileProject(newValues));
  };

  const handleSelectOwnerAgency = (value) => {
    const newValues = {
      name: "owner_agency",
      value: value,
    };
    dispatch(updateCertifyProfileProject(newValues));
  };

  const handleSelectProgram = (value) => {
    const newValues = {
      name: "program",
      value: value,
    };
    dispatch(updateCertifyProfileProject(newValues));
  };

  const handleSelectMainProject = (value) => {
    const newValues = {
      name: "main_project",
      value: value,
    };
    dispatch(updateCertifyProfileProject(newValues));
  };

  useEffect(() => {
    if (isEmpty(project)) return;

    switch (project.stages) {
      case "4": // UPEN
        setShowColumn({
          jppn: false,
          cabinet: false,
        });
        break;

      case "5": // JPPN
        setShowColumn({
          jppn: true,
          cabinet: false,
        });
        break;

      case "6": // CABINET
        setShowColumn({
          jppn: true,
          cabinet: true,
        });
        break;
    }

    const newValues = {
      name: project.name,
      project_type: {
        ...project.project_type,
        label: project?.project_type?.name,
        value: project?.project_type?.name,
      },
      project_category: project?.project_category,
      priority: project.priority,
      description: project?.description,
      key_performance_indicator: project.key_performance_indicator,
      justification: project.justification,
      sustainable_development_goals: project.sustainable_development_goals,
      main_sector: {
        ...project.main_sector,
        label: project?.main_sector?.name,
        value: project?.main_sector?.name,
      },
      strategic_directions: project.strategic_directions?.map(
        ({ created_at, updated_at, data, ...rest }) => ({
          ...rest,
          data: data
            ? (({ created_at, updated_at, ...cleanedData }) => cleanedData)(
                data,
              )
            : null,
        }),
      ),
      upen_section: project?.upen_section,
      executing_agency: project?.executing_agency,
      financially_responsible_agency: project?.financially_responsible_agency,
      owner_agency: project?.owner_agency,
      applicant_agency: project?.applicant_agency,
      program: project?.program,
      main_project: project?.main_project,
    };

    setStrategicDirectionOptions(project.main_sector?.strategic_directions);

    dispatch(createCertifyProfileProject(newValues));

    const filteredPrograms = programs?.filter(
      (obj) => obj.ministry_code.toString() === project.ministry_id.toString(),
    );
    setProgramOptions(filteredPrograms);

    const filteredMainProjects = mainProjects?.filter(
      (obj) =>
        obj?.ministry_id?.toString() === project?.ministry_id?.toString(),
    );
    setMainProjectOptions(filteredMainProjects);

    // setProgramValue(project.program);
    // setMainProjectValue(project.main_project);
    // setSubProjectCodeValue(project.code);
  }, [project, programs, mainProjects]);

  // useEffect(() => {
  //   if (isEmpty(project)) return;
  //   const filteredPrograms = programs?.filter(
  //     (obj) => obj.ministry_code.toString() === project.ministry_id.toString(),
  //   );
  //   setProgramOptions(filteredPrograms);

  //   const filteredMainProjects = mainProjects?.filter(
  //     (obj) =>
  //       obj?.ministry_id?.toString() === project?.ministry_id?.toString(),
  //   );
  //   setMainProjectOptions(filteredMainProjects);

  //   setProgramValue(project.program);
  //   setMainProjectValue(project.main_project);
  //   setSubProjectCodeValue(project.code);
  // }, [project, programs, mainProjects]);

  // console.log(project);

  return (
    <React.Fragment>
      <Card>
        <Card.Body>
          <h4 className='text-uppercase'>Maklumat Projek</h4>
          <div className='mb-3'>
            <div className='text-sm'>Kementerian</div>
            <Card.Text className='fw-bolder'>
              {project?.ministry?.label}
            </Card.Text>
          </div>
          <div className='mb-3'>
            <div className='text-sm'>Jabatan atau Agensi</div>
            <Card.Text className='fw-bolder'>
              {project?.department?.label}
            </Card.Text>
          </div>
          <div className='table-responsive'>
            <Table size='sm' bordered>
              <thead className='custom-table-head' style={{ fontSize: 12 }}>
                <tr>
                  <th style={{ width: "8%" }} />
                  <th style={{ width: "23%" }}>DIPOHON</th>
                  <th style={{ width: "23%" }}>DIPERAKU UPEN</th>
                  {showColumn.jppn && (
                    <th style={{ width: "23%" }}>DIPERAKU JPPN</th>
                  )}
                  {showColumn.cabinet && (
                    <th style={{ width: "23%" }}>DIPERAKU KABINET</th>
                  )}
                </tr>
              </thead>
              <tbody style={{ fontSize: 12 }}>
                {/* Program */}
                <tr>
                  <th className='align-middle'>Program</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.program?.name
                    }
                  </td>
                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.program?.label) ||
                          ""}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        placeholder='Pilih Program'
                        options={programOptions}
                        value={certifyProjectProfile?.program}
                        onChange={(value) => handleSelectProgram(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.program?.label}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          placeholder='Pilih Program'
                          options={programOptions}
                          value={certifyProjectProfile?.program}
                          onChange={(value) => handleSelectProgram(value)}
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        placeholder='Pilih Program'
                        options={programOptions}
                        value={certifyProjectProfile?.program}
                        onChange={(value) => handleSelectProgram(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>

                {/* Projek Utama */}
                <tr>
                  <th className='align-middle'>Projek Utama</th>
                  <td>
                    {(() => {
                      const stage = project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      );
                      return `${stage?.main_project?.code ?? ""} - ${
                        stage?.main_project?.name ?? ""
                      }`;
                    })()}
                  </td>
                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.main_project?.label) ||
                          ""}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        placeholder='Pilih Projek Utama'
                        options={mainProjectOptions}
                        value={certifyProjectProfile?.main_project}
                        onChange={(value) => handleSelectMainProject(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.main_project?.label}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          placeholder='Pilih Projek Utama'
                          options={mainProjectOptions}
                          value={certifyProjectProfile?.main_project}
                          onChange={(value) => handleSelectMainProject(value)}
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        placeholder='Pilih Projek Utama'
                        options={mainProjectOptions}
                        value={certifyProjectProfile?.main_project}
                        onChange={(value) => handleSelectMainProject(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>

                {/* Kod SUb Projek */}
                {/* <tr>
                  <th className='align-middle'>Kod Sub Projek</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.code
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.code
                        }
                      </>
                    ) : (
                      <Form.Group className='w-100'>
                        <Form.Control
                          type='text'
                          name='name'
                          value={certifyProjectProfile?.code || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "name",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.code
                          }
                        </>
                      ) : (
                        <Form.Group className='w-100'>
                          <Form.Control
                            type='text'
                            name='code'
                            value={certifyProjectProfile?.code || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const newValues = {
                                name: "code",
                                value: value,
                              };
                              dispatch(updateCertifyProfileProject(newValues));
                            }}
                          />
                        </Form.Group>
                      )}
                    </td>
                  )}
                  {showColumn.cabinet && (
                    <td>
                      <Form.Group className='w-100'>
                        <Form.Control
                          type='text'
                          name='code'
                          value={certifyProjectProfile?.code || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "code",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    </td>
                  )}
                </tr> */}

                <tr>
                  <th className='align-middle'>Nama Projek</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.name
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.name
                        }
                      </>
                    ) : (
                      <Form.Group className='w-100'>
                        <Form.Control
                          as='textarea'
                          name='name'
                          style={{
                            minHeight: 80,
                            maxHeight: 180,
                          }}
                          value={certifyProjectProfile?.name || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "name",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.name
                          }
                        </>
                      ) : (
                        <Form.Group className='w-100'>
                          <Form.Control
                            as='textarea'
                            name='name'
                            style={{
                              minHeight: 80,
                              maxHeight: 180,
                            }}
                            value={certifyProjectProfile?.name || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const newValues = {
                                name: "name",
                                value: value,
                              };
                              dispatch(updateCertifyProfileProject(newValues));
                            }}
                          />
                        </Form.Group>
                      )}
                    </td>
                  )}
                  {showColumn.cabinet && (
                    <td>
                      <Form.Group className='w-100'>
                        <Form.Control
                          as='textarea'
                          name='name'
                          style={{
                            minHeight: 80,
                            maxHeight: 180,
                          }}
                          value={certifyProjectProfile?.name || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "name",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    </td>
                  )}
                </tr>
                <tr>
                  <th className='align-middle'>Jenis Projek</th>
                  <td>
                    {!isEmpty(project.project_profile_stages) &&
                      (
                        project.project_profile_stages?.find(
                          (obj) => obj.status === 2 && obj.stages === 4,
                        )?.project_type || "null"
                      )?.label}
                  </td>
                  <td>
                    {showColumn.jppn ? (
                      <>
                        {!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.project_type?.label}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        options={[
                          {
                            id: 1,
                            label: "Fizikal",
                            value: "Fizikal",
                          },
                          {
                            id: 2,
                            label: "Program (Fizikal)",
                            value: "Program (Fizikal)",
                          },
                          {
                            id: 3,
                            label: "Program (Bukan Fizikal)",
                            value: "Program (Bukan Fizikal)",
                          },
                        ]}
                        value={certifyProjectProfile?.project_type}
                        onChange={(value) => {
                          const newValues = {
                            name: "project_type",
                            value: value,
                          };
                          dispatch(updateCertifyProfileProject(newValues));
                        }}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            (
                              project.project_profile_stages?.find(
                                (obj) => obj.status === 3 && obj.stages === 6,
                              )?.project_type || "null"
                            )?.label}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          options={[
                            {
                              label: "Fizikal",
                              value: 1,
                            },
                            {
                              label: "Program (Fizikal)",
                              value: 2,
                            },
                            {
                              label: "Program (Bukan Fizikal)",
                              value: 3,
                            },
                          ]}
                          value={certifyProjectProfile?.project_type}
                          onChange={(value) => {
                            const newValues = {
                              name: "project_type",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        options={[
                          {
                            label: "Fizikal",
                            value: 1,
                          },
                          {
                            label: "Program (Fizikal)",
                            value: 2,
                          },
                          {
                            label: "Program (Bukan Fizikal)",
                            value: 3,
                          },
                        ]}
                        value={certifyProjectProfile?.project_type}
                        onChange={(value) => {
                          const newValues = {
                            name: "project_type",
                            value: value,
                          };
                          dispatch(updateCertifyProfileProject(newValues));
                        }}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>
                <tr>
                  <th className='align-middle'>Kategori Projek</th>
                  <td>
                    {!isEmpty(project.project_profile_stages) &&
                      (
                        project.project_profile_stages?.find(
                          (obj) => obj.status === 2 && obj.stages === 4,
                        )?.project_category || "null"
                      )?.label}
                  </td>
                  <td>
                    {showColumn.jppn ? (
                      <>
                        {!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.project_category?.label}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        options={projectCategories}
                        value={certifyProjectProfile?.project_category}
                        onChange={(value) => {
                          const newValues = {
                            name: "project_category",
                            value: value,
                          };
                          dispatch(updateCertifyProfileProject(newValues));
                        }}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            (
                              project.project_profile_stages?.find(
                                (obj) => obj.status === 3 && obj.stages === 6,
                              )?.project_category || "null"
                            )?.label}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          options={projectCategories}
                          value={certifyProjectProfile?.project_category}
                          onChange={(value) => {
                            const newValues = {
                              name: "project_category",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        options={projectCategories}
                        value={certifyProjectProfile?.project_category}
                        onChange={(value) => {
                          const newValues = {
                            name: "project_category",
                            value: value,
                          };
                          dispatch(updateCertifyProfileProject(newValues));
                        }}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>
                <tr>
                  <th className='align-middle'>Keutamaan Projek</th>
                  <td>
                    {
                      project.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.priority
                    }
                  </td>
                  <td>
                    {showColumn.jppn ? (
                      <>
                        {
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.priority
                        }
                      </>
                    ) : (
                      <Select
                        isSearchable
                        options={priorityOptions}
                        value={priorityOptions.find(
                          (option) =>
                            option.value === certifyProjectProfile?.priority,
                        )}
                        onChange={(selected) => {
                          // handle your change logic
                          const newValues = {
                            name: "priority",
                            value: selected.value,
                          };
                          dispatch(updateCertifyProfileProject(newValues));
                        }}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.priority
                          }
                        </>
                      ) : (
                        <Select
                          isSearchable
                          options={priorityOptions}
                          value={priorityOptions.find(
                            (option) =>
                              option.value === certifyProjectProfile?.priority,
                          )}
                          onChange={(selected) => {
                            // handle your change logic
                            const newValues = {
                              name: "priority",
                              value: selected.value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        options={priorityOptions}
                        value={priorityOptions.find(
                          (option) =>
                            option.value === certifyProjectProfile?.priority,
                        )}
                        onChange={(selected) => {
                          // handle your change logic
                          const newValues = {
                            name: "priority",
                            value: selected.value,
                          };
                          dispatch(updateCertifyProfileProject(newValues));
                        }}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>
                <tr>
                  <th className='align-middle'>Keterangan Projek</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.description
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.description
                        }
                      </>
                    ) : (
                      <Form.Group className='w-100'>
                        <Form.Control
                          as='textarea'
                          name='description'
                          style={{
                            minHeight: 80,
                            maxHeight: 180,
                          }}
                          value={certifyProjectProfile?.description || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "description",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.description
                          }
                        </>
                      ) : (
                        <Form.Group className='w-100'>
                          <Form.Control
                            as='textarea'
                            name='description'
                            style={{
                              minHeight: 80,
                              maxHeight: 180,
                            }}
                            value={certifyProjectProfile?.description || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const newValues = {
                                name: "description",
                                value: value,
                              };
                              dispatch(updateCertifyProfileProject(newValues));
                            }}
                          />
                        </Form.Group>
                      )}
                    </td>
                  )}
                  {showColumn.cabinet && (
                    <td>
                      <Form.Group className='w-100'>
                        <Form.Control
                          as='textarea'
                          name='description'
                          style={{
                            minHeight: 80,
                            maxHeight: 180,
                          }}
                          value={certifyProjectProfile?.description || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "description",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    </td>
                  )}
                </tr>
                <tr>
                  <th className='align-middle'>KPI Program dan Projek</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.key_performance_indicator
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.key_performance_indicator
                        }
                      </>
                    ) : (
                      <Form.Group className='w-100'>
                        <Form.Control
                          as='textarea'
                          name='key_performance_indicator'
                          style={{
                            minHeight: 80,
                            maxHeight: 180,
                          }}
                          value={
                            certifyProjectProfile?.key_performance_indicator ||
                            ""
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "key_performance_indicator",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.key_performance_indicator
                          }
                        </>
                      ) : (
                        <Form.Group className='w-100'>
                          <Form.Control
                            as='textarea'
                            name='key_performance_indicator'
                            style={{
                              minHeight: 80,
                              maxHeight: 180,
                            }}
                            value={
                              certifyProjectProfile?.key_performance_indicator ||
                              ""
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              const newValues = {
                                name: "key_performance_indicator",
                                value: value,
                              };
                              dispatch(updateCertifyProfileProject(newValues));
                            }}
                          />
                        </Form.Group>
                      )}
                    </td>
                  )}
                  {showColumn.cabinet && (
                    <td>
                      <Form.Group className='w-100'>
                        <Form.Control
                          as='textarea'
                          name='key_performance_indicator'
                          style={{
                            minHeight: 80,
                            maxHeight: 180,
                          }}
                          value={
                            certifyProjectProfile?.key_performance_indicator ||
                            ""
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "key_performance_indicator",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    </td>
                  )}
                </tr>
                <tr>
                  <th className='align-middle'>Justifikasi</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.justification
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.justification
                        }
                      </>
                    ) : (
                      <Form.Group className='w-100'>
                        <Form.Control
                          as='textarea'
                          name='justification'
                          style={{
                            minHeight: 80,
                            maxHeight: 180,
                          }}
                          value={certifyProjectProfile?.justification || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "justification",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.justification
                          }
                        </>
                      ) : (
                        <Form.Group className='w-100'>
                          <Form.Control
                            as='textarea'
                            name='justification'
                            style={{
                              minHeight: 80,
                              maxHeight: 180,
                            }}
                            value={certifyProjectProfile?.justification || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const newValues = {
                                name: "justification",
                                value: value,
                              };
                              dispatch(updateCertifyProfileProject(newValues));
                            }}
                          />
                        </Form.Group>
                      )}
                    </td>
                  )}
                  {showColumn.cabinet && (
                    <td>
                      <Form.Group className='w-100'>
                        <Form.Control
                          as='textarea'
                          name='justification'
                          style={{
                            minHeight: 80,
                            maxHeight: 180,
                          }}
                          value={certifyProjectProfile?.justification || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newValues = {
                              name: "justification",
                              value: value,
                            };
                            dispatch(updateCertifyProfileProject(newValues));
                          }}
                        />
                      </Form.Group>
                    </td>
                  )}
                </tr>
                <tr>
                  <th className='align-middle'>
                    <span className='fst-italic'>
                      Sustainable Development Goals
                    </span>{" "}
                    (SDG)
                  </th>
                  <td>
                    {(() => {
                      const target = project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      );

                      if (!target?.sustainable_development_goals) return null;

                      try {
                        return (
                          <Row>
                            {target.sustainable_development_goals?.map(
                              (item, index) => {
                                return (
                                  <Col
                                    key={index}
                                    xs={4}
                                    sm={3}
                                    className='d-flex justify-content-center my-2'
                                  >
                                    <div
                                      className='position-relative'
                                      style={{
                                        height: "auto",
                                        width: 220,
                                      }}
                                    >
                                      <img
                                        className='mb-1 flex-fill w-100 h-100'
                                        alt='image_not_found'
                                        src={goalIcons[item]}
                                      />
                                    </div>
                                  </Col>
                                );
                              },
                            )}
                          </Row>
                        );
                      } catch (e) {
                        console.error("JSON parse error:", e);
                        return "Invalid data";
                      }
                    })()}
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(() => {
                          const target = project?.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          );

                          if (!target?.sustainable_development_goals)
                            return null;

                          try {
                            return (
                              <Row>
                                {target.sustainable_development_goals?.map(
                                  (item, index) => {
                                    return (
                                      <Col
                                        key={index}
                                        xs={4}
                                        sm={3}
                                        className='d-flex justify-content-center my-2'
                                      >
                                        <div
                                          className='position-relative'
                                          style={{
                                            height: "auto",
                                            width: 220,
                                          }}
                                        >
                                          <img
                                            className='mb-1 flex-fill w-100 h-100'
                                            alt='image_not_found'
                                            src={goalIcons[item]}
                                          />
                                        </div>
                                      </Col>
                                    );
                                  },
                                )}
                              </Row>
                            );
                          } catch (e) {
                            console.error("JSON parse error:", e);
                            return "Invalid data";
                          }
                        })()}
                      </>
                    ) : (
                      <Row className=''>
                        {goals?.map((item, idx) => {
                          const isSelected = (
                            certifyProjectProfile?.sustainable_development_goals ||
                            []
                          ).includes(item.index_no);

                          const bounceClass = isSelected
                            ? "bounce-in"
                            : "bounce-out";
                          return (
                            <Col
                              key={idx}
                              xs={4}
                              sm={3}
                              className='d-flex justify-content-center my-2'
                            >
                              <div
                                className='position-relative card-animated'
                                style={{
                                  cursor: "pointer",
                                  height: "auto",
                                  width: 130,
                                }}
                                onClick={() => handleSelectGoal(item)}
                              >
                                <img
                                  className='mb-1 flex-fill w-100 h-100'
                                  alt='image_not_found'
                                  src={goalIcons[item.index_no]}
                                />
                                <div
                                  className={`position-absolute ${bounceClass}`}
                                  style={{
                                    bottom: 10,
                                    right: 10,
                                    width: "20%",
                                    height: "20%",
                                    borderRadius: "50%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "#263238",
                                  }}
                                >
                                  <Icon path={mdiCheck} color='#FFFFFF' />
                                </div>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {(() => {
                            const target =
                              project?.project_profile_stages?.find(
                                (obj) => obj.status === 3 && obj.stages === 6,
                              );

                            if (!target?.sustainable_development_goals)
                              return null;

                            try {
                              return (
                                <Row>
                                  {target.sustainable_development_goals?.map(
                                    (item, index) => {
                                      return (
                                        <Col
                                          key={index}
                                          xs={4}
                                          sm={3}
                                          className='d-flex justify-content-center my-2'
                                        >
                                          <div
                                            className='position-relative'
                                            style={{
                                              height: "auto",
                                              width: 220,
                                            }}
                                          >
                                            <img
                                              className='mb-1 flex-fill w-100 h-100'
                                              alt='image_not_found'
                                              src={goalIcons[item]}
                                            />
                                          </div>
                                        </Col>
                                      );
                                    },
                                  )}
                                </Row>
                              );
                            } catch (e) {
                              console.error("JSON parse error:", e);
                              return "Invalid data";
                            }
                          })()}
                        </>
                      ) : (
                        <Row className=''>
                          {goals?.map((item, idx) => {
                            const isSelected = (
                              certifyProjectProfile?.sustainable_development_goals ||
                              []
                            ).includes(item.index_no);

                            const bounceClass = isSelected
                              ? "bounce-in"
                              : "bounce-out";
                            return (
                              <Col
                                key={idx}
                                xs={4}
                                sm={3}
                                className='d-flex justify-content-center my-2'
                              >
                                <div
                                  className='position-relative card-animated'
                                  style={{
                                    cursor: "pointer",
                                    height: "auto",
                                    width: 130,
                                  }}
                                  onClick={() => handleSelectGoal(item)}
                                >
                                  <img
                                    className='mb-1 flex-fill w-100 h-100'
                                    alt='image_not_found'
                                    src={goalIcons[item.index_no]}
                                  />
                                  <div
                                    className={`position-absolute ${bounceClass}`}
                                    style={{
                                      bottom: 10,
                                      right: 10,
                                      width: "20%",
                                      height: "20%",
                                      borderRadius: "50%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      backgroundColor: "#263238",
                                    }}
                                  >
                                    <Icon path={mdiCheck} color='#FFFFFF' />
                                  </div>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Row className=''>
                        {goals?.map((item, idx) => {
                          const isSelected = (
                            certifyProjectProfile?.sustainable_development_goals ||
                            []
                          ).includes(item.index_no);

                          const bounceClass = isSelected
                            ? "bounce-in"
                            : "bounce-out";
                          return (
                            <Col
                              key={idx}
                              xs={4}
                              sm={3}
                              className='d-flex justify-content-center my-2'
                            >
                              <div
                                className='position-relative card-animated'
                                style={{
                                  cursor: "pointer",
                                  height: "auto",
                                  width: 130,
                                }}
                                onClick={() => handleSelectGoal(item)}
                              >
                                <img
                                  className='mb-1 flex-fill w-100 h-100'
                                  alt='image_not_found'
                                  src={goalIcons[item.index_no]}
                                />
                                <div
                                  className={`position-absolute ${bounceClass}`}
                                  style={{
                                    bottom: 10,
                                    right: 10,
                                    width: "20%",
                                    height: "20%",
                                    borderRadius: "50%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "#263238",
                                  }}
                                >
                                  <Icon path={mdiCheck} color='#FFFFFF' />
                                </div>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    </td>
                  )}
                </tr>

                <tr>
                  <th className='align-middle'>Bidang Keutamaan</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.main_sector?.name
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.main_sector?.label) ||
                          ""}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        options={mainSectorOptions}
                        value={certifyProjectProfile?.main_sector}
                        onChange={(value) => handleSelectPriorityArea(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.main_sector?.label}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          options={mainSectorOptions}
                          value={certifyProjectProfile?.main_sector}
                          onChange={(value) => handleSelectPriorityArea(value)}
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        options={mainSectorOptions}
                        value={certifyProjectProfile?.main_sector}
                        onChange={(value) => handleSelectPriorityArea(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>

                <tr>
                  <th className='align-middle'>Keterangan Bidang Keutamaan</th>
                  <td>
                    {(() => {
                      const upenStages = project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.strategic_directions;

                      return (
                        <ul>
                          {upenStages?.map((obj) => {
                            return <li key={obj.id}>{obj.data?.name}</li>;
                          })}
                        </ul>
                      ); // or return null/empty element
                    })()}
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(() => {
                          const jppnStages =
                            project?.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 5,
                            )?.strategic_directions;

                          return (
                            <ul>
                              {jppnStages?.map((obj, index) => {
                                return <li key={index}>{obj.data?.name}</li>;
                              })}
                            </ul>
                          ); // or return null/empty element
                        })()}
                      </>
                    ) : (
                      <>
                        {strategicDirectionOptions?.map((item, idx) => {
                          const isChecked =
                            certifyProjectProfile.strategic_directions
                              ?.map((obj) => obj.strategic_direction_id)
                              ?.includes(item.id);

                          return (
                            <Form.Group
                              key={item.id}
                              controlId={item.id}
                              className='mt-2'
                            >
                              <Form.Check
                                name='strategic_directions'
                                label={item.name}
                                value={item.id}
                                type='checkbox'
                                onChange={(e) =>
                                  handleCheckPriorityAreaItem(e, item)
                                }
                                checked={isChecked}
                              />
                            </Form.Group>
                          );
                        })}
                      </>
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {(() => {
                            const jppnStages =
                              project?.project_profile_stages?.find(
                                (obj) => obj.status === 3 && obj.stages === 6,
                              )?.strategic_directions;

                            return (
                              <ul>
                                {jppnStages?.map((obj) => {
                                  return <li key={obj.id}>{obj.data?.name}</li>;
                                })}
                              </ul>
                            ); // or return null/empty element
                          })()}
                        </>
                      ) : (
                        <>
                          {strategicDirectionOptions?.map((item, idx) => {
                            const isChecked =
                              certifyProjectProfile.strategic_directions
                                ?.map((obj) => obj.strategic_direction_id)
                                ?.includes(item.id);

                            return (
                              <Form.Group
                                key={item.id}
                                controlId={item.id}
                                className='mt-2'
                              >
                                <Form.Check
                                  name='strategic_directions'
                                  label={item.name}
                                  value={item.id}
                                  type='checkbox'
                                  onChange={(e) =>
                                    handleCheckPriorityAreaItem(e, item)
                                  }
                                  checked={isChecked}
                                />
                              </Form.Group>
                            );
                          })}
                        </>
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <>
                        {strategicDirectionOptions?.map((item, idx) => {
                          const isChecked =
                            certifyProjectProfile.strategic_directions
                              ?.map((obj) => obj.strategic_direction_id)
                              ?.includes(item.id);

                          return (
                            <Form.Group
                              key={item.id}
                              controlId={item.id}
                              className='mt-2'
                            >
                              <Form.Check
                                name='strategic_directions'
                                label={item.name}
                                value={item.id}
                                type='checkbox'
                                onChange={(e) =>
                                  handleCheckPriorityAreaItem(e, item)
                                }
                                checked={isChecked}
                              />
                            </Form.Group>
                          );
                        })}
                      </>
                    </td>
                  )}
                </tr>

                <tr>
                  <th className='align-middle'>Seksyen UPEN</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.upen_section?.name
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.upen_section?.label) ||
                          ""}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        options={sectionOptions}
                        value={certifyProjectProfile?.upen_section}
                        onChange={(value) => handleSelectUpenSection(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.upen_section?.label}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          options={sectionOptions}
                          value={certifyProjectProfile?.upen_section}
                          onChange={(value) => handleSelectUpenSection(value)}
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        options={sectionOptions}
                        value={certifyProjectProfile?.upen_section}
                        onChange={(value) => handleSelectUpenSection(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>

                <tr>
                  <th className='align-middle'>Agensi Pemohon</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.applicant_agency?.value
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.applicant_agency?.value) ||
                          ""}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        options={departmentOptions}
                        value={certifyProjectProfile?.applicant_agency}
                        onChange={(value) => handleSelectApplicantAgency(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.applicant_agency?.value}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          options={departmentOptions}
                          value={certifyProjectProfile?.applicant_agency}
                          onChange={(value) =>
                            handleSelectApplicantAgency(value)
                          }
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        options={departmentOptions}
                        value={certifyProjectProfile?.applicant_agency}
                        onChange={(value) => handleSelectApplicantAgency(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>

                <tr>
                  <th className='align-middle'>Agensi Pelaksana</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.executing_agency?.value
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.executing_agency?.value) ||
                          ""}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        options={departmentOptions}
                        value={certifyProjectProfile?.executing_agency}
                        onChange={(value) => handleSelectExecutingAgency(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.executing_agency?.value}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          options={departmentOptions}
                          value={certifyProjectProfile?.executing_agency}
                          onChange={(value) =>
                            handleSelectExecutingAgency(value)
                          }
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        options={departmentOptions}
                        value={certifyProjectProfile?.executing_agency}
                        onChange={(value) => handleSelectExecutingAgency(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>

                <tr>
                  <th className='align-middle'>
                    Agensi Bertanggungjawab (Kewangan)
                  </th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.financially_responsible_agency?.value
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.financially_responsible_agency?.value) ||
                          ""}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        options={departmentOptions}
                        value={
                          certifyProjectProfile?.financially_responsible_agency
                        }
                        onChange={(value) => handleSelectFinancialAgency(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.financially_responsible_agency?.value}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          options={departmentOptions}
                          value={
                            certifyProjectProfile?.financially_responsible_agency
                          }
                          onChange={(value) =>
                            handleSelectFinancialAgency(value)
                          }
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        options={departmentOptions}
                        value={
                          certifyProjectProfile?.financially_responsible_agency
                        }
                        onChange={(value) => handleSelectFinancialAgency(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>

                <tr>
                  <th className='align-middle'>Agensi Pemilik</th>
                  <td>
                    {
                      project?.project_profile_stages?.find(
                        (obj) => obj.status === 2 && obj.stages === 4,
                      )?.owner_agency?.value
                    }
                  </td>

                  <td>
                    {showColumn.jppn ? (
                      <>
                        {(!isEmpty(project.project_profile_stages) &&
                          project.project_profile_stages?.find(
                            (obj) => obj.status === 3 && obj.stages === 5,
                          )?.owner_agency?.value) ||
                          ""}
                      </>
                    ) : (
                      <Select
                        isSearchable
                        options={departmentOptions}
                        value={certifyProjectProfile?.owner_agency}
                        onChange={(value) => handleSelectOwnerAgency(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    )}
                  </td>

                  {showColumn.jppn && (
                    <td>
                      {showColumn.jppn && showColumn.cabinet ? (
                        <>
                          {!isEmpty(project.project_profile_stages) &&
                            project.project_profile_stages?.find(
                              (obj) => obj.status === 3 && obj.stages === 6,
                            )?.owner_agency?.value}
                        </>
                      ) : (
                        <Select
                          isSearchable
                          options={departmentOptions}
                          value={certifyProjectProfile?.owner_agency}
                          onChange={(value) => handleSelectOwnerAgency(value)}
                          noOptionsMessage={() => "Tiada pilihan"}
                          components={{
                            IndicatorSeparator: () => null,
                          }}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: "#ced4da",
                              backgroundColor: state.isDisabled
                                ? "#e2e8ee"
                                : "#FFF",
                            }),
                            singleValue: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change text color to black when disabled
                            }),
                            placeholder: (baseStyles, state) => ({
                              ...baseStyles,
                              color: state.isDisabled
                                ? "rgb(84, 84, 84)"
                                : "#495057", // Change placeholder color to black when disabled
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999, // bring to front
                            }),
                          }}
                        />
                      )}
                    </td>
                  )}

                  {showColumn.cabinet && (
                    <td>
                      <Select
                        isSearchable
                        options={departmentOptions}
                        value={certifyProjectProfile?.owner_agency}
                        onChange={(value) => handleSelectOwnerAgency(value)}
                        noOptionsMessage={() => "Tiada pilihan"}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: "#ced4da",
                            backgroundColor: state.isDisabled
                              ? "#e2e8ee"
                              : "#FFF",
                          }),
                          singleValue: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change text color to black when disabled
                          }),
                          placeholder: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isDisabled
                              ? "rgb(84, 84, 84)"
                              : "#495057", // Change placeholder color to black when disabled
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999, // bring to front
                          }),
                        }}
                      />
                    </td>
                  )}
                </tr>
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
      {/* <Card>
        <Card.Body>
          <div className='text-sm'>Nama Projek</div>
          <h4 className='fw-bolder'>{project?.name}</h4>
          <div className='text-sm'>Program</div>
          <h4 className='fw-bolder'>{project?.program?.name}</h4>
          <Row>
            <Col md={6}>
              <div className='mb-1'>
                <div className='text-sm'>Jenis Projek</div>
                <Card.Text className='fw-bolder'>
                  {project?.project_type?.name}
                </Card.Text>
              </div>
              <div className='mb-1'>
                <div className='text-sm'>Kategori Projek</div>
                <Card.Text className='fw-bolder'>
                  {project?.project_category?.label}
                </Card.Text>
              </div>
              <div className='mb-1'>
                <div className='text-sm'>Keutamaan Projek</div>
                <Card.Text className='fw-bolder'>{project?.priority}</Card.Text>
              </div>
            </Col>
            <Col md={6}>
              <div className='mb-1'>
                <div className='text-sm'>Kementerian</div>
                <Card.Text className='fw-bolder'>
                  {project?.ministry?.label}
                </Card.Text>
              </div>
              <div className='mb-1'>
                <div className='text-sm'>Jabatan atau Agensi</div>
                <Card.Text className='fw-bolder'>
                  {project?.department?.label}
                </Card.Text>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card> */}
    </React.Fragment>
  );
};

export default ProjectInfo;
